import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Container,
} from '@mui/material';
import {
  Assessment as MeritListIcon,
  Article as NoticeBoardIcon,
  ReceiptLong as TcListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MeritListManagement from './MeritListManagement';
import NoticeboardListManagement from './NoticeboardListManagement';
import TcManagement from './TcManagement';

const ServicesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeService, setActiveService] = useState<string | null>(null);

  const services = [
    {
      id: 'merit-list',
      title: t('merit_list'),
      description: t('download_and_manage_merit_lists'),
      icon: <MeritListIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      actions: [
        { label: t('get_merit_list'), action: 'get-merit-list' }
      ]
    },
    {
      id: 'notice-board',
      title: t('notice_board_list'),
      description: t('download_and_manage_notice_board_lists'),
      icon: <NoticeBoardIcon sx={{ fontSize: 40, color: '#2e7d32' }} />,
      actions: [
        { label: t('get_notice_board_list'), action: 'get-notice-board-list' }
      ]
    },
    {
      id: 'tc-list',
      title: t('tc_list'),
      description: t('download_and_manage_transfer_certificates'),
      icon: <TcListIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
      actions: [
        { label: t('get_tc_list'), action: 'get-tc-list' }
      ]
    }
  ];

  const handleServiceClick = (serviceId: string) => {
    setActiveService(serviceId);
  };

  const handleActionClick = (action: string, serviceId: string) => {
    setActiveService(serviceId);
    // The service content will be rendered based on activeService state
  };


  const renderServiceContent = () => {
    switch (activeService) {
      case 'merit-list':
        return <MeritListManagement />;
      case 'notice-board':
        return <NoticeboardListManagement />;
      case 'tc-list':
        return <TcManagement />;
      default:
        return (
          <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4, color: '#333' }}>
              {t('services_dashboard')}
            </Typography>
            <Grid container spacing={4}>
              {services.map((service) => (
                <Grid item xs={12} md={4} key={service.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      borderRadius: 3,
                      boxShadow: 3,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 8,
                      }
                    }}
                    onClick={() => handleServiceClick(service.id)}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                      <Box sx={{ 
                        mb: 3, 
                        p: 2, 
                        borderRadius: '50%', 
                        backgroundColor: service.id === 'merit-list' ? '#e3f2fd' : 
                                        service.id === 'notice-board' ? '#e8f5e8' : '#ffebee',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {service.icon}
                      </Box>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                        {service.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {service.actions.map((action, index) => (
                          <Button
                            key={index}
                            variant="contained"
                            size="large"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActionClick(action.action, service.id);
                            }}
                            sx={{ 
                              minWidth: '160px',
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: 'none',
                              fontSize: '16px',
                              fontWeight: 500,
                              backgroundColor: service.id === 'merit-list' ? '#1976d2' : 
                                            service.id === 'notice-board' ? '#2e7d32' : '#d32f2f',
                              '&:hover': {
                                backgroundColor: service.id === 'merit-list' ? '#1565c0' : 
                                              service.id === 'notice-board' ? '#1b5e20' : '#c62828',
                                transform: 'scale(1.02)',
                              }
                            }}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        );
    }
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: 3, 
      minHeight: '100vh',
      backgroundColor: '#f8f9fa'
    }}>
      {activeService && (
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={() => setActiveService(null)}
            sx={{ 
              mb: 2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 1.5,
              borderColor: '#ddd',
              color: '#666',
              '&:hover': {
                borderColor: '#1976d2',
                color: '#1976d2',
                backgroundColor: '#e3f2fd'
              }
            }}
          >
{t('back_to_services')}
          </Button>
        </Box>
      )}
      {renderServiceContent()}
    </Box>
  );
};

export default ServicesDashboard;
